import * as React from 'react';
import memoizeOne from 'memoize-one';
import {
  DataRow,
  DataTable,
  DateFormatterCollection,
  formatValueAsMetricNumber,
  measureDomainToRender,
  MetricBlue,
  MetricBlueLight,
  timeDomainToRender,
  maxPrecision,
  dropRowsWithUndefinedAccessorResult,
  ValueAccessor,
  MetricComparisonColor,
  extractDatumFromVictoryRow,
  undefinedToNull,
  MetricsGrey,
  DateRange,
} from '@tableau/metrics-engine';
import { Messages } from '../../gen-src/Localize';
import { style } from 'typestyle';
import { TabStyles } from '@tableau/widgets';
import { TimeSeriesInteractiveLayer } from './TimeSeriesInteractiveLayer';
import { TimeSeriesProps } from '../TimeSeriesProps';
import { withSize } from 'react-sizeme';
import { VictoryPortal, VictoryAxis, VictoryChart, VictoryLine, VictoryLabel, VictoryContainer } from 'victory';

interface DetailTimeSeriesProps extends TimeSeriesProps {
  readonly size: { readonly width: number; readonly height: number };
  readonly dateFormatter: DateFormatterCollection;
  readonly formatString?: string;
  readonly timestampAccessor: (point: DataRow) => Date | undefined;
  readonly measureAccessor: (point: DataRow) => number;
  readonly comparisonAccessor?: ValueAccessor<number | undefined>;
  readonly shouldRenderReferenceLine?: boolean;
  readonly comparisonLineStyle?: React.CSSProperties;
  readonly comparisonLineLabelAccessor?: ValueAccessor<string | undefined>;
  readonly comparisonLineInteractiveDot?: boolean;
  readonly colorIndicatorsEnabled: boolean;
  readonly colorAccessor: ValueAccessor<string>;
  readonly windowRange?: DateRange;
  /**
   * We pass in a `JSON.stringified` version of the `EffectiveDefinition`
   * because we treat the `TimeSeriesInteractiveLayer` as a "fully uncontrolled
   * component", that manages it's own selected value. We need to be able to
   * re-create it from scratch and reset internal state whenever the definition
   * changes, as it does when editing the configuration.
   */
  readonly stringifiedDefinitionKey: string;
}

// We have to define this here in order to have axis labels render in the correct font.
// Since they are SVGs, they don't inherit styles like the rest of the text elements do.
const fontFamily = 'Benton Sans';

// Using typestyle to target the root SVG node created by victory.
// Victory doesn't expose this for styling directly, so we need to target it
// as a child of the containing <div>.
const chartContainerStyle = style({
  $nest: {
    svg: {
      overflow: 'visible',
    },
  },
});

export class DetailTimeSeries extends React.Component<DetailTimeSeriesProps> {
  public render(): JSX.Element {
    return (
      <div
        aria-label={Messages.ariaTimeseriesInteractiveDescription()}
        style={{
          position: 'relative',
          height: '100%',
          touchAction: 'pan-y',
        }}
      >
        <div
          data-tb-test-id={this.props.testId || 'detail-time-series'}
          aria-hidden
          className={chartContainerStyle}
          style={{
            ...this.props.style,
            width: '100%',
            height: '100%',
            position: 'absolute',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
          }}
        >
          {this.renderChart()}
        </div>
      </div>
    );
  }

  public shouldComponentUpdate(nextProps: DetailTimeSeriesProps): boolean {
    return (
      this.props.data !== nextProps.data ||
      this.props.size.width !== nextProps.size.width ||
      this.props.size.height !== nextProps.size.height
    );
  }

  private renderChart = (): JSX.Element | null => {
    const height = this.props.size.height;
    const width = this.props.size.width;

    const currentData = dropRowsWithUndefinedAccessorResult(this.props.data, this.props.measureAccessor);
    const comparisonData =
      this.props.comparisonAccessor &&
      dropRowsWithUndefinedAccessorResult(this.props.data, this.props.comparisonAccessor);
    const domainAndRange = this.calculateDomainAndRange(this.props.data, height, width);

    if (!domainAndRange) {
      return null;
    }

    const { domain, range } = domainAndRange;
    return (
      <>
        <VictoryChart
          scale={{ x: 'time', y: 'linear' }}
          height={height}
          width={width}
          padding={chartPadding}
          domain={domain}
          range={range}
          containerComponent={<VictoryContainer responsive={false} />}
        >
          {/* x axis */}
          <VictoryAxis
            crossAxis={false}
            style={{
              tickLabels: {
                fill: TabStyles.Typography!.Color!.PrimaryMedium!,
                fontSize: '12px',
                fontFamily: fontFamily,
                fontWeight: 800,
              },
              axis: { stroke: 'none', strokeWidth: 0 },
            }}
            standalone={false}
            offsetY={X_AXIS_HEIGHT}
            fixLabelOverlap
            orientation={'bottom'}
            range={range}
            tickFormat={this.props.dateFormatter.axisTick}
          />
          {/* y axis */}
          <VictoryAxis
            dependentAxis
            crossAxis={false}
            name={'y-axis'}
            tickLabelComponent={<VictoryLabel dy={-8} dx={Y_AXIS_WIDTH} />}
            tickFormat={this.formatTick}
            style={{
              grid: { stroke: MetricBlueLight },
              tickLabels: {
                fill: TabStyles.Typography!.Color!.PrimaryMedium!,
                fontSize: '12px',
                fontFamily: fontFamily,
              },
              axis: { stroke: 'none' },
            }}
            fixLabelOverlap
            offsetX={chartPadding.left}
            range={range}
          />
          {this.props.shouldRenderReferenceLine && this.props.comparisonAccessor && (
            <VictoryLine
              // `data` isn't typed as readonly, hence this cast
              data={comparisonData as DataRow[]}
              x={this.props.timestampAccessor}
              y={this.props.comparisonAccessor}
              style={{
                data: {
                  stroke: MetricComparisonColor,
                  strokeWidth: 3,
                  ...this.props.comparisonLineStyle,
                },
              }}
              labelComponent={
                <VictoryPortal>
                  <VictoryLabel dx={20} dy={7} style={comparisonLineLabelStyle} />
                </VictoryPortal>
              }
              labels={
                this.props.comparisonLineLabelAccessor
                  ? extractDatumFromVictoryRow(undefinedToNull(this.props.comparisonLineLabelAccessor))
                  : undefined
              }
            />
          )}
          {/* trend line */}
          <VictoryLine
            data={currentData as DataRow[]}
            name={'line'}
            // @ts-ignore: Victory doesn't allow data accessor functions to return Date or undefined. This is fixed in version 34.2.0.
            x={this.props.timestampAccessor}
            y={this.props.measureAccessor}
            style={{
              data: {
                stroke: this.props.colorIndicatorsEnabled ? MetricsGrey : MetricBlue,
                strokeWidth: 2,
              },
            }}
          />
        </VictoryChart>
        <TimeSeriesInteractiveLayer
          data={this.props.data}
          tooltipFormatter={this.props.dateFormatter.tooltip}
          domain={domain}
          size={this.props.size}
          padding={dataAreaPadding}
          onDataPointActivated={this.handleActivated}
          measureAccessor={this.props.measureAccessor}
          timestampAccessor={this.props.timestampAccessor}
          comparisonAccessor={this.props.comparisonAccessor}
          shouldRenderComparisonDot={this.props.comparisonLineInteractiveDot}
          colorIndicatorsEnabled={this.props.colorIndicatorsEnabled}
          colorAccessor={this.props.colorAccessor}
          key={this.props.stringifiedDefinitionKey}
        />
      </>
    );
  };

  private formatTick = (value?: number): string => {
    return formatValueAsMetricNumber(value, this.props.formatString, 6);
  };

  // This function calculates the adjusted domain and ranges to use for chart,
  // and is memoized to avoid recomputing when the data and size haven't
  // changed.
  // No typedef because I'd just have to repeat the signature twice.
  // tslint:disable-next-line: typedef
  private calculateDomainAndRange = memoizeOne((data: DataTable, height: number, width: number) => {
    const dataHeight = height - (dataAreaPadding.top + dataAreaPadding.bottom);

    const timeDomain = this.props.windowRange
      ? [this.props.windowRange.first, this.props.windowRange.last]
      : timeDomainToRender(data);

    const accessors = [this.props.measureAccessor, this.props.comparisonAccessor];
    const measureDomain = measureDomainToRender(data, maxPrecision(this.props.formatString), accessors);
    const measureRange: [number, number] = [dataHeight + dataAreaPadding.top, dataAreaPadding.top];
    const timeRange: [number, number] = [dataAreaPadding.left, width - dataAreaPadding.right];

    return timeDomain[0] === undefined
      ? undefined
      : {
          domain: {
            x: timeDomain as [Date, Date],
            y: measureDomain as [number, number],
          },
          range: { x: timeRange, y: measureRange },
        };
  });

  private handleActivated = (point: DataRow) => {
    if (this.props.onActivated) {
      this.props.onActivated(point);
    }
  };
}

export const ResizableDetailTimeSeries = withSize({
  monitorHeight: true,
  monitorWidth: true,
})(DetailTimeSeries);

const chartPadding = {
  top: 50,
  left: 5,
  right: 0,
  bottom: 0,
};

const Y_AXIS_WIDTH = 50;
const X_AXIS_HEIGHT = 30;
const RIGHT_DATA_MARGIN = 40;

const dataAreaPadding = {
  top: chartPadding.top,
  left: chartPadding.left + Y_AXIS_WIDTH,
  right: chartPadding.right + RIGHT_DATA_MARGIN,
  bottom: chartPadding.bottom + X_AXIS_HEIGHT,
};

const comparisonLineLabelStyle: React.CSSProperties = {
  fontFamily: fontFamily,
  fill: TabStyles.Typography!.Color!.PrimaryMedium!,
  fontSize: 14,
  textAnchor: 'start',
};
